import type { FieldMapping } from 'models/models';

export type RequiredFirstFileIntraopField = keyof Pick<
	FieldMapping,
	| 'facility'
	| 'patient_in_room'
	| 'patient_out_room'
	| 'room'
	| 'surgeon'
	| 'scheduled_start'
	| 'procedure_start'
	| 'procedure_stop'
	| 'encounter_type'
	| 'service_line'
>;

export type OptionalFirstFileIntraopField = keyof Pick<
	FieldMapping,
	| 'client_provided_case_id'
	| 'surgery_date'
	| 'procedure_description'
	| 'anesthesia_start'
	| 'anesthesia_end'
	| 'primary_anesthesiologist'
	| 'primary_anesthetist'
	| 'asa_class'
	| 'anesthesia_ready'
	| 'is_add_on'
	| 'case_create_date'
	| 'case_cancel_date'
	| 'scheduled_stop'
	| 'delay_code'
	| 'delay_type'
	| 'delay_reason'
	| 'delay_duration'
	| 'scheduled_room'
	| 'cpt_code'
	| 'cpt_description'
	| 'primary_payor'
	| 'primary_circulator'
	| 'primary_cst'
	| 'primary_first_assist'
	| 'preop_in'
	| 'pacu_p1_in'
	| 'pacu_p1_out'
	| 'pacu_p2_in'
	| 'pacu_p2_out'
>;

export type FirstFileIntraopField = RequiredFirstFileIntraopField | OptionalFirstFileIntraopField;

export interface FirstFileIntraopFieldDefinition {
	key: FirstFileIntraopField;
	label: string;
	description: string;
	aliases: string[];
	required: boolean;
}

export const requiredFirstFileIntraopFields: FirstFileIntraopFieldDefinition[] = [
	{
		key: 'facility',
		label: 'Facility',
		description: 'Name of the facility where the surgical case occurred.',
		aliases: ['facility', 'facility name', 'hospital', 'hospital name', 'location'],
		required: true,
	},
	{
		key: 'patient_in_room',
		label: 'Patient In Room',
		description: 'Time the patient was wheeled into the operating room. Also known as "wheels in" time.',
		aliases: ['patient in room', 'patient in', 'wheels in', 'in room'],
		required: true,
	},
	{
		key: 'patient_out_room',
		label: 'Patient Out Room',
		description: 'Time the patient was wheeled out of the operating room. Also known as "wheels out" time.',
		aliases: ['patient out room', 'patient out', 'wheels out', 'out room'],
		required: true,
	},
	{
		key: 'room',
		label: 'Room',
		description: 'Name of the operating room where the surgical case occurred.',
		aliases: ['room', 'room name', 'operating room', 'or room'],
		required: true,
	},
	{
		key: 'surgeon',
		label: 'Surgeon',
		description: 'Name of the primary surgeon performing the surgery.',
		aliases: ['surgeon', 'primary surgeon', 'surgeon name'],
		required: true,
	},
	{
		key: 'scheduled_start',
		label: 'Scheduled Start',
		description: 'Time the case was scheduled to start.',
		aliases: ['scheduled start', 'schedule start', 'scheduled time'],
		required: true,
	},
	{
		key: 'procedure_start',
		label: 'Procedure Start',
		description: 'Time the surgical procedure begins. Also known as "cut time".',
		aliases: ['procedure start', 'surgery start', 'cut time'],
		required: true,
	},
	{
		key: 'procedure_stop',
		label: 'Procedure Stop',
		description: 'Time the surgical procedure ends. Also known as "close time".',
		aliases: ['procedure stop', 'procedure end', 'surgery end', 'close time'],
		required: true,
	},
	{
		key: 'encounter_type',
		label: 'Encounter Type',
		description:
			'A categorical description of the patient encounter, like “Inpatient”, “Outpatient”, or “Same Day Admit”',
		aliases: ['encounter type', 'patient type', 'patient class', 'visit type'],
		required: true,
	},
	{
		key: 'service_line',
		label: 'Procedure Service Line',
		description:
			'A categorical description of the service line or specialty for the case performed, like “Orthopedics” or ”Urology”',
		aliases: ['service line', 'procedure service line', 'surgical case specialty', 'specialty'],
		required: true,
	},
];

export const optionalFirstFileIntraopFields: FirstFileIntraopFieldDefinition[] = [
	{
		key: 'client_provided_case_id',
		label: 'Case ID',
		description: 'Client provided case identifier that will be used to link scheduled cases to intraoperative records.',
		aliases: ['case id', 'case identifier', 'client provided case id', 'encounter id'],
		required: false,
	},
	{
		key: 'surgery_date',
		label: 'Surgery Date',
		description: 'Date on which the surgical case was performed.',
		aliases: ['surgery date', 'case date', 'procedure date', 'date of surgery'],
		required: false,
	},
	{
		key: 'procedure_description',
		label: 'Procedure Description',
		description: 'Text description of the procedure(s) performed during the case.',
		aliases: ['procedure description', 'procedure name', 'procedure', 'case procedure'],
		required: false,
	},
	{
		key: 'anesthesia_start',
		label: 'Anesthesia Start',
		description: 'Date and/or time the anesthesia provider begins providing anesthesia.',
		aliases: ['anesthesia start', 'anesthesia begin', 'anes start'],
		required: false,
	},
	{
		key: 'anesthesia_end',
		label: 'Anesthesia End',
		description: 'Date and/or time the anesthesia provider stops providing anesthesia.',
		aliases: ['anesthesia end', 'anesthesia stop', 'anes end'],
		required: false,
	},
	{
		key: 'primary_anesthesiologist',
		label: 'Primary Anesthesiologist',
		description: 'Name of primary anesthesiologist for the case.',
		aliases: ['primary anesthesiologist', 'anesthesiologist', 'primary anesthesia'],
		required: false,
	},
	{
		key: 'primary_anesthetist',
		label: 'Primary Anesthetist',
		description: 'Name of primary anesthestist for the case.',
		aliases: ['primary anesthetist', 'anesthetist', 'primary crna', 'crna'],
		required: false,
	},
	{
		key: 'asa_class',
		label: 'ASA Class',
		description: 'A categorical description of the patient’s health on the day of surgery.',
		aliases: ['asa class', 'asa classification', 'asa status'],
		required: false,
	},
	{
		key: 'anesthesia_ready',
		label: 'Anesthesia Ready',
		description: 'Date and/or time when pre-operative anesthesia activities are complete.',
		aliases: ['anesthesia ready', 'anes ready'],
		required: false,
	},
	{
		key: 'is_add_on',
		label: 'Add-On Indicator',
		description: 'Indicator designating a case as an add-on.',
		aliases: ['add on indicator', 'add on', 'is add on', 'addon'],
		required: false,
	},
	{
		key: 'case_create_date',
		label: 'Case Create Date',
		description: 'Date the case record was created.',
		aliases: ['case create date', 'case creation date', 'record create date', 'created date'],
		required: false,
	},
	{
		key: 'case_cancel_date',
		label: 'Case Cancel Date',
		description:
			'Date the case was canceled. Some cases are canceled and rescheduled, so more information is typically required for reliable cancelation information.',
		aliases: ['case cancel date', 'case cancellation date', 'cancel date', 'canceled date'],
		required: false,
	},
	{
		key: 'scheduled_stop',
		label: 'Scheduled Stop',
		description: 'Time the case was scheduled to conclude.',
		aliases: ['scheduled stop', 'scheduled end', 'schedule stop', 'schedule end'],
		required: false,
	},
	{
		key: 'delay_code',
		label: 'Delay Code',
		description: 'An alpha/numeric code for the type of delay that occurred.',
		aliases: ['delay code', 'case delay code'],
		required: false,
	},
	{
		key: 'delay_type',
		label: 'Delay Type',
		description: 'A categorical description of the delay reason, e.g. "Surgeon Late", "Patient Delay".',
		aliases: ['delay type', 'delay category', 'case delay type'],
		required: false,
	},
	{
		key: 'delay_reason',
		label: 'Delay Reason',
		description:
			'A free-text description of the delay reason. This is typically used when the "Delay Type" or "Delay Code" does not adequately describe the source of the delay.',
		aliases: ['delay reason', 'delay description', 'case delay reason'],
		required: false,
	},
	{
		key: 'delay_duration',
		label: 'Delay Duration',
		description: 'A measurement of the time of the delay. Only integer values can be parsed if this field is mapped.',
		aliases: ['delay duration', 'delay minutes', 'delay time'],
		required: false,
	},
	{
		key: 'scheduled_room',
		label: 'Scheduled Room',
		description:
			'The room where the case was scheduled to occur. May be different than where the case actually happened, e.g. "Room" field above.',
		aliases: ['scheduled room', 'schedule room', 'planned room'],
		required: false,
	},
	{
		key: 'cpt_code',
		label: 'CPT Code',
		description:
			'The standardized code that designates services between providers and payors. This field will represent any code category (I, II, III, etc.).',
		aliases: ['cpt code', 'procedure code', 'billing code'],
		required: false,
	},
	{
		key: 'cpt_description',
		label: 'CPT Description',
		description: 'The metadata associated with the CPT Code field above.',
		aliases: ['cpt description', 'cpt name', 'procedure code description'],
		required: false,
	},
	{
		key: 'primary_payor',
		label: 'Primary Payor',
		description:
			'The insurance policy or entity that pays first when a patient is covered by more than one insurance plan.',
		aliases: ['primary payor', 'primary payer', 'payor', 'payer'],
		required: false,
	},
	{
		key: 'primary_circulator',
		label: 'Primary Circulator',
		description: 'The name of the primary nurse circulator for the case.',
		aliases: ['primary circulator', 'circulator', 'nurse circulator'],
		required: false,
	},
	{
		key: 'primary_cst',
		label: 'Primary CST',
		description: 'The name of the primary CST for the case.',
		aliases: ['primary cst', 'cst', 'surgical technologist', 'surgical tech'],
		required: false,
	},
	{
		key: 'primary_first_assist',
		label: 'First Assist',
		description: 'The name of the primary first assist for the case.',
		aliases: ['first assist', 'primary first assist', 'first assistant'],
		required: false,
	},
	{
		key: 'preop_in',
		label: 'Preop In',
		description: 'Time the patient entered preop.',
		aliases: ['preop in', 'pre op in', 'preoperative in'],
		required: false,
	},
	{
		key: 'pacu_p1_in',
		label: 'PACU Phase I In',
		description: 'Time the patient entered PACU Phase I.',
		aliases: ['pacu phase i in', 'pacu phase 1 in', 'pacu 1 in'],
		required: false,
	},
	{
		key: 'pacu_p1_out',
		label: 'PACU Phase I Out',
		description: 'Time the patient left PACU Phase I.',
		aliases: ['pacu phase i out', 'pacu phase 1 out', 'pacu 1 out'],
		required: false,
	},
	{
		key: 'pacu_p2_in',
		label: 'PACU Phase II In',
		description: 'Time the patient entered PACU Phase II.',
		aliases: ['pacu phase ii in', 'pacu phase 2 in', 'pacu 2 in'],
		required: false,
	},
	{
		key: 'pacu_p2_out',
		label: 'PACU Phase II Out',
		description: 'Time the patient left PACU Phase II.',
		aliases: ['pacu phase ii out', 'pacu phase 2 out', 'pacu 2 out'],
		required: false,
	},
];

export const firstFileIntraopFields = [...requiredFirstFileIntraopFields, ...optionalFirstFileIntraopFields];

export const firstFileIntraopFieldsByKey = Object.fromEntries(
	firstFileIntraopFields.map((field) => [field.key, field])
) as Record<FirstFileIntraopField, FirstFileIntraopFieldDefinition>;
